/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import { CardId } from '../../../../models/diviCard';
import DebouncedTextField from '../../../customs/debouncedTextField/DebouncedTextField';
import CheckboxList from '../../../customs/checkboxList/CheckboxList';
import { NodeType } from '../../../../backendModels/report.model';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { InputState } from '../../../../backendModels/general.model';
import {
  MEDICAL_CONDITION_ABDOMEN_ELEMENT_KEY,
  MedicalConditionAbdomenRecord,
  getAbdomenTupelsFor,
  MedicalConditionAbdomenKeys,
} from '../../../../models/genericElements/medicalConditions/abdomen';
import { Draft } from 'immer';
import { checkParent, uncheckChildren } from '../../../../utils/util';
import { isGenericRecordDeletable, setGenericRecordToNormal } from '../../../../models/generic';

const checkboxes1: [MedicalConditionAbdomenKeys, string][] = getAbdomenTupelsFor([
  MedicalConditionAbdomenKeys.akutesAbdomen,
  MedicalConditionAbdomenKeys.giBlutung,
]);

const checkboxes2: [MedicalConditionAbdomenKeys, string][] = getAbdomenTupelsFor([
  MedicalConditionAbdomenKeys.obereGiBlutung,
  MedicalConditionAbdomenKeys.untereGiBlutung,
]);

const checkboxes3: [MedicalConditionAbdomenKeys, string][] = getAbdomenTupelsFor([
  MedicalConditionAbdomenKeys.kolik,
  MedicalConditionAbdomenKeys.enteritis,
]);

interface MedicalConditionAbdomenProps {
  nodeType: NodeType;
}

export default function MedicalConditionAbdomen({ nodeType }: MedicalConditionAbdomenProps) {
  const { findRecordOrDefault, adaptRecord } = useContext(ReportsAPIContext);

  const record = findRecordOrDefault(
    'generic',
    nodeType,
    MEDICAL_CONDITION_ABDOMEN_ELEMENT_KEY
  ) as MedicalConditionAbdomenRecord;
  const conditions = record.inputState === InputState.ENTERED ? (record.values.erkrankungenAbdomen?.options ?? []) : [];

  const conditionsChange = (newConditions: MedicalConditionAbdomenKeys[]) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<MedicalConditionAbdomenRecord>, deleteRecord) => {
        if (newConditions.length > 0) {
          draft.inputState = InputState.ENTERED;
          draft.values = {
            ...draft.values,
            erkrankungenAbdomen: {
              fieldType: 'multiSelect',
              options: newConditions,
            },
          };
        } else {
          delete draft.values?.erkrankungenAbdomen;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      MEDICAL_CONDITION_ABDOMEN_ELEMENT_KEY
    );
  };
  const otherChange = (value: string) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<MedicalConditionAbdomenRecord>, deleteRecord) => {
        if (value !== '') {
          draft.inputState = InputState.ENTERED;
          draft.values = {
            ...draft.values,
            erkrankungenAbdomenSonstige: {
              fieldType: 'text',
              text: value,
            },
          };
        } else {
          delete draft.values?.erkrankungenAbdomenSonstige;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      MEDICAL_CONDITION_ABDOMEN_ELEMENT_KEY
    );
  };

  return (
    <DiviFieldCard
      cardType={CardId.MedicalConditionAbdomen}
      nodeType={nodeType}
      recordType='generic'
      elementKey={MEDICAL_CONDITION_ABDOMEN_ELEMENT_KEY}
      normalStateButtonVisible={false}
      onNormalStateButtonClicked={() =>
        setGenericRecordToNormal(adaptRecord, nodeType, MEDICAL_CONDITION_ABDOMEN_ELEMENT_KEY)
      }>
      <div className='symptoms-list'>
        <CheckboxList
          items={checkboxes1}
          selectedValues={conditions}
          onValuesChange={(newConditions) =>
            conditionsChange(
              uncheckChildren(newConditions, MedicalConditionAbdomenKeys.giBlutung, [
                MedicalConditionAbdomenKeys.obereGiBlutung,
                MedicalConditionAbdomenKeys.untereGiBlutung,
              ])
            )
          }
        />
        <CheckboxList
          className='indented'
          items={checkboxes2}
          selectedValues={conditions}
          onValuesChange={(newConditions) =>
            conditionsChange(
              checkParent(newConditions, MedicalConditionAbdomenKeys.giBlutung, [
                MedicalConditionAbdomenKeys.obereGiBlutung,
                MedicalConditionAbdomenKeys.untereGiBlutung,
              ])
            )
          }
        />
        <CheckboxList items={checkboxes3} selectedValues={conditions} onValuesChange={conditionsChange} />
      </div>
      <DebouncedTextField
        fullWidth
        label='Sonstige...'
        value={record.inputState === InputState.ENTERED ? (record.values.erkrankungenAbdomenSonstige?.text ?? '') : ''}
        onDebounceChange={otherChange}
        inputProps={{ maxLength: 18 }}
      />
    </DiviFieldCard>
  );
}
